import { User } from "@privy-io/react-auth";
import Cookies from "js-cookie";
import { COOKIE_NAME } from "@/constants/middleware.constants";

export interface BackendMergeConflictData {
	sourceUserId: string;
	targetUserId: string;
	sourceProfile: { userName?: string; drops: number; snags: number };
	targetProfile: { userName?: string; drops: number; snags: number };
}

class BackendMergeRequiredError extends Error {
	mergeConflict: BackendMergeConflictData;

	constructor(mergeConflict: BackendMergeConflictData) {
		super("Merge required");
		this.name = "BackendMergeRequiredError";
		this.mergeConflict = mergeConflict;
	}
}

function isBackendMergeRequiredError(
	error: unknown
): error is BackendMergeRequiredError {
	return error instanceof BackendMergeRequiredError;
}

/** Returns true when the page is served over HTTPS (or forced via env). */
function isSecureContext(): boolean {
	if (typeof window === "undefined") return true;
	return (
		window.location.protocol === "https:" ||
		process.env.NEXT_PUBLIC_FORCE_SECURE_COOKIES === "true" ||
		process.env.NEXT_PUBLIC_ENV === "production" ||
		process.env.NODE_ENV === "production"
	);
}

/**
 * Privy Authentication Service
 * Handles user authentication and account linking with the backend.
 */
export class PrivyAuthService {
	private static instance: PrivyAuthService;

	public static getInstance(): PrivyAuthService {
		if (!PrivyAuthService.instance) {
			PrivyAuthService.instance = new PrivyAuthService();
		}
		return PrivyAuthService.instance;
	}

	/**
	 * Process Privy user authentication with backend.
	 * Collects all linked Solana wallets and social handles from the Privy user
	 * and sends them to /api/user/auth/privy-login for a full sync.
	 */
	async authenticateWithBackend(privyUser: User): Promise<any | null> {
		try {
			// Collect ALL linked Solana wallets (not just the first)
			const solanaWallets = privyUser.linkedAccounts.filter(
				(account) =>
					(account.type === "wallet" || account.type === "smart_wallet") &&
					(account as any).chainType === "solana"
			);

			if (solanaWallets.length === 0) {
				console.error("privy-auth: No Solana wallet found for Privy user:", {
					availableAccounts: privyUser.linkedAccounts.map((acc) => ({
						type: acc.type,
						chainType: (acc as any).chainType,
					})),
				});
				return null;
			}

			// Primary wallet is the first Solana wallet (Privy embedded or first linked)
			const primaryWallet = solanaWallets[0];
			const publicKey =
				(primaryWallet as any).address ||
				(primaryWallet as any).walletAddress;

			// All linked wallets for the linkedWallets[] array
			const linkedWallets = solanaWallets.map((w) => ({
				address: (w as any).address || (w as any).walletAddress,
				walletType: (w as any).walletClientType || "privy",
			}));

		// Extract social identity handles from linkedAccounts
		const twitterAccount = privyUser.linkedAccounts.find(
			(a) => a.type === "twitter_oauth"
		);
		const googleAccount = privyUser.linkedAccounts.find(
			(a) => a.type === "google_oauth"
		);
		const discordAccount = privyUser.linkedAccounts.find(
			(a) => a.type === "discord_oauth"
		);

		// Twitter profile picture: Privy appends `_normal` for 48×48px.
		// Strip it to get the original full-size image.
		const rawTwitterPic = (twitterAccount as any)?.profilePictureUrl as string | null | undefined;
		const twitterProfileImage = rawTwitterPic
			? rawTwitterPic.replace(/_normal(\.[^./]+)$/, "$1").replace(/_normal$/, "")
			: null;

		// Best available profile image — only Twitter provides one via Privy.
		const socialProfileImage = twitterProfileImage || null;

		// Email: primary Privy email first, then Google (always has one), then Discord.
		const primaryEmail = privyUser.email?.address || null;
		const googleEmail = (googleAccount as any)?.email as string | null | undefined ?? null;
		const discordEmail = (discordAccount as any)?.email as string | null | undefined ?? null;
		// Prefer the primary Privy email; fall back to social provider email
		const resolvedEmail = primaryEmail || googleEmail || discordEmail || null;

		const authRequest = {
			privyUserId: privyUser.id,
			publicKey,
			walletType: (primaryWallet as any).walletClientType || "privy",
			email: resolvedEmail,
			phone: privyUser.phone?.number || null,
			linkedWallets,
			twitterUsername: (twitterAccount as any)?.username || null,
			twitterName: (twitterAccount as any)?.name || null,
			googleName:
				(googleAccount as any)?.name ||
				(googleAccount as any)?.email ||
				null,
			discordUsername: (discordAccount as any)?.username || null,
			socialProfileImage,
		};

			const response = await fetch("/api/user/auth/privy-login", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(authRequest),
			});

			const data = await response.json();

			if (!response.ok) {
				if (response.status === 409 && data?.mergeRequired) {
					throw new BackendMergeRequiredError({
						sourceUserId: data.sourceUserId,
						targetUserId: data.targetUserId,
						sourceProfile: data.sourceProfile,
						targetProfile: data.targetProfile,
					});
				}

				console.error("privy-auth: Backend returned error:", {
					status: response.status,
					message: data?.message || data?.error || response.statusText,
					fullResponse: data,
				});
				throw new Error(
					data?.message || `HTTP ${response.status}: ${response.statusText}`
				);
			}

			if (data.status === true) {
				const backendUser = data.data;
				// Persist the fresh user + JWT so subsequent API calls are authorised.
				// This mirrors what usePrivyWallet does in its authenticateWithBackend
				// effect, ensuring syncWithRetry leaves a valid cookie behind.
				if (backendUser) {
					Cookies.set(COOKIE_NAME, JSON.stringify(backendUser), {
						expires: 1,
						secure: isSecureContext(),
						sameSite: "lax",
						path: "/",
					});
				}
				return backendUser;
			} else {
				console.error("privy-auth: Backend authentication failed:", data.message);
				return null;
			}
		} catch (error) {
			if (isBackendMergeRequiredError(error)) {
				throw error;
			}

			console.error("privy-auth: Authentication error:", error);
			return null;
		}
	}

	/**
	 * Authenticate with backend with retry and exponential backoff.
	 * Clears the cookie before each attempt so a stale cookie never blocks re-sync.
	 * Returns { success: true } or { success: false, error: string }.
	 */
	async syncWithRetry(
		privyUser: User,
		maxAttempts = 3
	): Promise<{
		success: boolean;
		error?: string;
		mergeConflict?: BackendMergeConflictData;
	}> {
		for (let attempt = 1; attempt <= maxAttempts; attempt++) {
			// Wait before retries only — attempt 1 fires immediately,
			// attempt 2 waits 1 s, attempt 3 waits 2 s.
			if (attempt > 1) {
				const delayMs = 1000 * Math.pow(2, attempt - 2);
				await new Promise((r) => setTimeout(r, delayMs));
			}

			try {
				Cookies.remove(COOKIE_NAME);
				const backendUser = await this.authenticateWithBackend(privyUser);
				if (backendUser) return { success: true };
			} catch (error) {
				if (isBackendMergeRequiredError(error)) {
					return {
						success: false,
						error:
							"Account linked in Privy. Merge your MyGeoTokens data to finish combining these accounts.",
						mergeConflict: error.mergeConflict,
					};
				}
				// continue to next attempt
			}
		}

		return {
			success: false,
			error: "Account updated but sync failed. Please refresh the page to see changes.",
		};
	}

	/**
	 * Get user wallet balance.
	 */
	async getWalletBalance(walletAddress: string): Promise<number> {
		try {
			const response = await fetch(
				`/api/wallet/balance?address=${walletAddress}`
			);
			const data = await response.json();
			if (data.success) {
				return parseFloat(data.balance);
			}
			return 0;
		} catch {
			return 0;
		}
	}
}

export const privyAuthService = PrivyAuthService.getInstance();
